import moment from 'moment';
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Discipline } from 'core/typings';
import { Image } from '../../typings';
import './styles.scss';
import LocalizationContext from '../../contexts/Localizations';
import { ImageBasic } from '../common';

interface CountdownProps {
  discipline?: Discipline | null;
  timestamp: string;
  areUnitsShortened?: boolean;
  tissotCountdownLogo?: Image | null;
  onElapse?: (() => void) | null;
}

const Countdown = ({
  discipline, timestamp, areUnitsShortened, tissotCountdownLogo, onElapse,
}: CountdownProps) => {
  const then = useMemo(() => moment.utc(timestamp), [timestamp]);
  const [durationRemaining, setDurationRemaining] = useState<moment.Duration | null>(null);
  const { translations } = useContext(LocalizationContext);
  const {
    daysLabel,
    hoursLabel,
    minutesLabel,
    secondsLabel,
  } = translations.countdown;

  useEffect(() => {
    const t = setInterval(() => {
      const now = moment.utc();
      const diff = Math.max(then.diff(now), 0);

      setDurationRemaining(moment.duration(diff));

      if (diff === 0) {
        clearInterval(t);
        onElapse?.();
      }
    }, 1000);

    return () => clearInterval(t);
  }, [timestamp]);

  const days = durationRemaining ? Math.floor(durationRemaining.asDays()) : '-';
  const hours = durationRemaining?.hours() ?? '-';
  const minutes = durationRemaining?.minutes() ?? '-';
  const seconds = durationRemaining?.seconds() ?? '-';

  const isDayZero = () => days === 0;
  const isHourZero = () => isDayZero() && hours === 0;
  const isMinuteZero = () => isHourZero() && minutes === 0;
  const isSecondZero = () => isMinuteZero() && seconds === 0;

  // TODO: l10n days/hours/minutes/seconds
  return (
    <>
      <div
        className={`countdown ${discipline ? `countdown--${discipline}` : ''} ${tissotCountdownLogo ? 'countdown__tissot' : ''}`}
        role="timer"
        aria-live="off"
        aria-label={`${days} days, ${hours} hours, ${minutes} minutes and ${seconds} seconds remaining`}
      >
        {tissotCountdownLogo && (
          <div className="countdown__logo">
            <ImageBasic image={tissotCountdownLogo} />
          </div>
        )}
        <div className="countdown__segments">
          <div className={`countdown__segment ${isDayZero() && 'countdown__segment--elapsed'}`} aria-hidden="true">
            <div className="countdown__segment__value">
              {days}
            </div>
            <div className="countdown__segment__unit">
              {areUnitsShortened ? daysLabel[0] : daysLabel}
            </div>
          </div>
          <div className={`countdown__segment ${isHourZero() && 'countdown__segment--elapsed'}`} aria-hidden="true">
            <div className="countdown__segment__value">
              {hours}
            </div>
            <div className="countdown__segment__unit">
              {areUnitsShortened ? hoursLabel[0] : hoursLabel}
            </div>
          </div>
          <div className={`countdown__segment ${isMinuteZero() && 'countdown__segment--elapsed'}`} aria-hidden="true">
            <div className="countdown__segment__value">
              {minutes}
            </div>
            <div className="countdown__segment__unit">
              {areUnitsShortened ? minutesLabel[0] : minutesLabel}
            </div>
          </div>
          <div className={`countdown__segment ${isSecondZero() && 'countdown__segment--elapsed'}`} aria-hidden="true">
            <div className="countdown__segment__value">
              {seconds}
            </div>
            <div className="countdown__segment__unit">
              {areUnitsShortened ? secondsLabel[0] : secondsLabel}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Countdown;
